import { Link } from "react-router-dom";
import useBlogPosts from "@/hooks/useBlogPosts";
import BlogCard from "./components/BlogCard";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";

export default function BlogPage() {
  const { publishedPosts, loaded } = useBlogPosts();

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Serena's Quiet Recovery Space Blog",
    description:
      "Gentle guidance, practical support, and compassionate insights for your recovery journey. Articles on reducing drinking, alcohol-free living, and building healthier habits.",
    url: "https://serenasquietrecoveryspace.com/blog",
    about: {
      "@type": "Organization",
      name: "Serena's Quiet Recovery Space",
    },
    blogPost: publishedPosts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      url: `https://serenasquietrecoveryspace.com/blog/${p.slug}`,
      image: p.coverImage,
      datePublished: p.createdAt,
      dateModified: p.updatedAt,
      author: {
        "@type": "Person",
        name: "Serena Raimes",
      },
    })),
  };

  return (
    <main className="min-h-screen pt-24 md:pt-28 pb-16 md:pb-20">
      <SEOMeta
        title="Blog | Alcohol Recovery Insights & Gentle Guidance | Serena's Quiet Recovery Space"
        description="Gentle guidance, practical support, and compassionate insights for your recovery journey. Articles on reducing drinking, emotional wellbeing, building new habits, and finding steadier ground."
        canonicalUrl="/blog"
        ogType="website"
        schema={blogSchema}
      />

      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Blog" },
            ]}
          />
          <header className="mb-12 md:mb-16">
            <div className="flex items-center justify-between mb-3">
              <p className="font-body text-xs md:text-sm text-foreground-500 uppercase tracking-widest">
                Blog
              </p>
              <Link
                to="/blog/admin"
                title="Manage posts"
                className="inline-flex items-center gap-1.5 font-body text-xs text-foreground-400 hover:text-foreground-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-edit-line text-base" />
                <span className="hidden sm:inline">Manage posts</span>
              </Link>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-light text-foreground-950 mb-4">
              Quiet reflections &amp; gentle guidance
            </h1>
            <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl">
              Practical support, compassionate insights, and quiet encouragement for your recovery journey.
              Explore articles on emotional wellbeing, building new habits, and finding steadier ground.
            </p>
          </header>

          {!loaded ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : publishedPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-foreground-500 text-sm">
                No posts yet. Check back soon.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {publishedPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}

          <div className="mt-16 pt-10 border-t border-background-200/70 text-center">
            <p className="font-body text-sm text-foreground-500 mb-4">
              Need more personalised support?
            </p>
            <Link
              to="/support"
              className="inline-block bg-primary-500 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Explore coaching options
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}